import { createPortal } from 'react-dom';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supportTitle } from '../shared/support/supportLinks';
import { useEventSupportContact } from '../shared/support/useEventSupportContact';
import { isLiveBoardPath } from '../features/b2b-live-board/live-board-session';
import SupportPageHelp from './SupportPageHelp';

export default function SupportWidget({ publicView = false }: { publicView?: boolean }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<'sidebar' | 'floating'>('floating');
  const [isCompactSidebar, setIsCompactSidebar] = useState(false);
  const [compactPanelStyle, setCompactPanelStyle] = useState<CSSProperties | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const support = useEventSupportContact(!publicView);
  const supportEmail = support.email;
  const supportManualUrl = support.manualUrl;
  const supportWhatsappDigits = support.whatsappDigits;
  const supportWhatsappDisplay = support.whatsappDisplay;
  const memoizedSupportLinks = support.links;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const updateCompactSidebarState = () => {
      const body = document.body;
      const sidebarStyle = body?.getAttribute('data-sidebar-style');
      const layout = body?.getAttribute('data-layout');
      setIsCompactSidebar(sidebarStyle === 'mini' && layout === 'vertical');
    };

    updateCompactSidebarState();

    const observer = new MutationObserver(updateCompactSidebarState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-sidebar-style', 'data-layout'],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    setPortalTarget(document.body);
    setPlacement('floating');
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isOpen || placement !== 'sidebar' || !isCompactSidebar) {
      setCompactPanelStyle(null);
      return undefined;
    }

    if (typeof window === 'undefined') return undefined;

    const updateCompactPanelStyle = () => {
      const triggerRect = triggerButtonRef.current?.getBoundingClientRect();

      if (!triggerRect) {
        setCompactPanelStyle({
          position: 'fixed',
          left: 'calc(6.25rem + 12px)',
          bottom: '16px',
          width: 'min(320px, calc(100vw - 132px))',
          borderColor: 'rgba(59, 130, 246, 0.12)',
          boxShadow: '0 10px 24px rgba(59, 130, 246, 0.08)',
          overflow: 'hidden',
          zIndex: 2147483647,
        });
        return;
      }

      setCompactPanelStyle({
        position: 'fixed',
        left: `${Math.max(12, triggerRect.right + 12)}px`,
        bottom: `${Math.max(12, window.innerHeight - triggerRect.bottom)}px`,
        width: 'min(320px, calc(100vw - 132px))',
        borderColor: 'rgba(59, 130, 246, 0.12)',
        boxShadow: '0 10px 24px rgba(59, 130, 246, 0.08)',
        overflow: 'hidden',
        zIndex: 2147483647,
      });
    };

    updateCompactPanelStyle();
    window.addEventListener('resize', updateCompactPanelStyle);
    window.addEventListener('scroll', updateCompactPanelStyle, true);

    return () => {
      window.removeEventListener('resize', updateCompactPanelStyle);
      window.removeEventListener('scroll', updateCompactPanelStyle, true);
    };
  }, [isOpen, placement, isCompactSidebar]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapperRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [isOpen]);

  if (!supportWhatsappDigits && !supportEmail && !supportManualUrl) {
    return null;
  }

  const widget = (
    <div
      ref={wrapperRef}
      data-testid="support-widget"
      data-placement={placement}
      className={`${placement === 'sidebar' ? 'position-relative d-flex w-100' : 'position-fixed d-flex'} flex-column ${placement === 'sidebar' ? (isCompactSidebar ? 'align-items-center' : 'align-items-stretch') : 'align-items-end'}`}
      style={
        placement === 'sidebar'
          ? {
            zIndex: 20,
            maxWidth: isCompactSidebar ? '100%' : 260,
            marginInline: 'auto',
          }
          : {
            right: 'clamp(12px, 1.5vw, 20px)',
            bottom: 'clamp(12px, 1.5vw, 20px)',
            zIndex: 2147483647,
            maxWidth: 'calc(100vw - 24px)',
          }
      }
    >
      {isOpen && placement === 'sidebar' ? (
        <div
          className={`mb-3 rounded-4 border bg-white${isCompactSidebar ? ' position-fixed' : ''}`}
          style={
            isCompactSidebar
              ? compactPanelStyle ?? {
                position: 'fixed',
                left: 'calc(6.25rem + 12px)',
                bottom: '16px',
                width: 'min(320px, calc(100vw - 132px))',
                borderColor: 'rgba(59, 130, 246, 0.12)',
                boxShadow: '0 10px 24px rgba(59, 130, 246, 0.08)',
                overflow: 'hidden',
                zIndex: 2147483647,
              }
              : {
                borderColor: 'rgba(59, 130, 246, 0.12)',
                boxShadow: '0 10px 24px rgba(59, 130, 246, 0.08)',
                overflow: 'hidden',
              }
          }
        >
          <div className="border-bottom px-3 py-2" style={{ borderColor: 'rgba(59, 130, 246, 0.08)' }}>
            <div className="d-flex min-w-0 align-items-center gap-2">
              <span
                className="d-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: 28,
                  height: 28,
                  background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                }}
              >
                <i className="bi bi-headset" style={{ fontSize: 14 }} />
              </span>
              <div className="min-w-0">
                <div className="text-truncate fw-semibold text-dark" style={{ fontSize: 13 }}>
                  {supportTitle}
                </div>
                <div className="text-truncate text-muted" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  Kanalla iletişime geçin
                </div>
              </div>
            </div>
          </div>

          <div className="d-grid gap-1 p-2">
            {supportWhatsappDigits ? (
              <a
                href={memoizedSupportLinks.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="d-flex min-w-0 align-items-center gap-2 rounded-3 px-2 py-2 text-decoration-none"
                style={{ background: 'rgba(37, 211, 102, 0.08)' }}
              >
                <span
                  className="d-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-circle text-white"
                  style={{ width: 30, height: 30, backgroundColor: '#25D366' }}
                >
                  <i className="bi bi-whatsapp" style={{ fontSize: 14 }} />
                </span>
                <span className="min-w-0 flex-grow-1">
                  <span className="d-block text-truncate fw-semibold text-dark" style={{ fontSize: 13 }}>
                    WhatsApp
                  </span>
                  <span className="d-block text-truncate text-muted" style={{ fontSize: 11 }}>
                    {supportWhatsappDisplay}
                  </span>
                </span>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-success" style={{ fontSize: 12 }} />
              </a>
            ) : null}

            {supportEmail ? (
              <a
                href={memoizedSupportLinks.email}
                className="d-flex min-w-0 align-items-center gap-2 rounded-3 px-2 py-2 text-decoration-none"
                style={{ background: 'rgba(59, 130, 246, 0.06)' }}
              >
                <span
                  className="d-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-circle text-white"
                  style={{ width: 30, height: 30, backgroundColor: 'var(--primary)' }}
                >
                  <i className="bi bi-envelope" style={{ fontSize: 14 }} />
                </span>
                <span className="min-w-0 flex-grow-1">
                  <span className="d-block text-truncate fw-semibold text-dark" style={{ fontSize: 13 }}>
                    E-posta
                  </span>
                  <span className="d-block text-truncate text-muted" style={{ fontSize: 11 }}>
                    {supportEmail}
                  </span>
                </span>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-primary" style={{ fontSize: 12 }} />
              </a>
            ) : null}

            {supportManualUrl ? (
              <a
                href={memoizedSupportLinks.manual}
                target="_blank"
                rel="noreferrer"
                className="d-flex min-w-0 align-items-center gap-2 rounded-3 px-2 py-2 text-decoration-none"
                style={{ background: 'rgba(245, 158, 11, 0.09)' }}
              >
                <span
                  className="d-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-circle text-white"
                  style={{ width: 30, height: 30, backgroundColor: '#f59e0b' }}
                >
                  <i className="bi bi-book" style={{ fontSize: 14 }} />
                </span>
                <span className="min-w-0 flex-grow-1">
                  <span className="d-block text-truncate fw-semibold text-dark" style={{ fontSize: 13 }}>
                    Kullanım Kılavuzu
                  </span>
                  <span className="d-block text-truncate text-muted" style={{ fontSize: 11 }}>
                    Yardım dokümanını aç
                  </span>
                </span>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-warning" style={{ fontSize: 12 }} />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {isOpen && placement !== 'sidebar' ? (
        <div
          className="mb-3 rounded-4 border border-light bg-white shadow-lg"
          style={{
            width: 'min(340px, calc(100vw - 24px))',
            maxHeight: 'calc(100vh - 96px)',
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          <div
            className="px-4 py-3 text-white"
            style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, #2563eb 100%)',
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25"
                style={{ width: 34, height: 34 }}
              >
                <i className="bi bi-headset fs-5" />
              </span>
              <div>
                <div className="fw-semibold">{supportTitle}</div>
                <small className="d-block opacity-75" style={{ lineHeight: 1.35 }}>
                  WhatsApp veya e-posta ile ulaşın
                </small>
              </div>
            </div>
          </div>

          {!publicView && <SupportPageHelp manualUrl={supportManualUrl} onNavigate={() => setIsOpen(false)} />}

          <div className="p-3 d-grid gap-2">
            {supportWhatsappDigits ? (
              <a
                href={memoizedSupportLinks.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="d-flex w-100 align-items-center justify-content-between rounded-3 border border-success-subtle px-3 py-3 text-decoration-none"
                style={{
                  background: 'rgba(37, 211, 102, 0.08)',
                }}
              >
                <div className="d-flex min-w-0 align-items-center gap-3">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle text-white"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: '#25D366',
                      flex: '0 0 auto',
                    }}
                  >
                    <i className="bi bi-whatsapp" />
                  </span>
                  <div className="min-w-0">
                    <div className="fw-semibold text-dark">WhatsApp</div>
                    <small className="d-block text-muted text-truncate">{supportWhatsappDisplay}</small>
                  </div>
                </div>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-success" />
              </a>
            ) : null}

            {supportEmail ? (
              <a
                href={memoizedSupportLinks.email}
                className="d-flex w-100 align-items-center justify-content-between rounded-3 border border-primary-subtle px-3 py-3 text-decoration-none"
                style={{
                  background: 'rgba(59, 130, 246, 0.06)',
                }}
              >
                <div className="d-flex min-w-0 align-items-center gap-3">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle text-white"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: 'var(--primary)',
                      flex: '0 0 auto',
                    }}
                  >
                    <i className="bi bi-envelope" />
                  </span>
                  <div className="min-w-0">
                    <div className="fw-semibold text-dark">E-posta</div>
                    <small className="d-block text-muted text-truncate">{supportEmail}</small>
                  </div>
                </div>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-primary" />
              </a>
            ) : null}

            {supportManualUrl ? (
              <a
                href={memoizedSupportLinks.manual}
                target="_blank"
                rel="noreferrer"
                className="d-flex w-100 align-items-center justify-content-between rounded-3 border px-3 py-3 text-decoration-none"
                style={{
                  background: 'rgba(245, 158, 11, 0.09)',
                  borderColor: 'rgba(245, 158, 11, 0.24)',
                }}
              >
                <div className="d-flex min-w-0 align-items-center gap-3">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle text-white"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: '#f59e0b',
                      flex: '0 0 auto',
                    }}
                  >
                    <i className="bi bi-book" />
                  </span>
                  <div className="min-w-0">
                    <div className="fw-semibold text-dark">Kullanım Kılavuzu</div>
                    <small className="d-block text-muted text-truncate">Yardım dokümanını aç</small>
                  </div>
                </div>
                <i className="bi bi-box-arrow-up-right flex-shrink-0 text-warning" />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      <button
        ref={triggerButtonRef}
        type="button"
        aria-label="Destek seçeneklerini aç"
        data-tour="support"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className={`btn btn-primary rounded-pill shadow-lg d-inline-flex align-items-center justify-content-center ${placement === 'sidebar' ? 'w-100' : ''}`}
        style={
          placement === 'sidebar'
            ? {
              background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
              borderColor: 'rgba(59, 130, 246, 0.12)',
              boxShadow: '0 10px 22px rgba(59, 130, 246, 0.16)',
              minHeight: isCompactSidebar ? 72 : 44,
              width: isCompactSidebar ? 72 : '100%',
              maxWidth: isCompactSidebar ? 72 : '100%',
              padding: isCompactSidebar ? '0' : '0.5rem 0.75rem',
              fontSize: isCompactSidebar ? '0.8rem' : '0.9rem',
              letterSpacing: '0',
              flexDirection: isCompactSidebar ? 'column' : 'row',
              gap: isCompactSidebar ? 4 : 8,
            }
            : {
              background: 'linear-gradient(135deg, var(--primary) 0%, #2563eb 100%)',
              borderColor: 'transparent',
              boxShadow: '0 16px 30px rgba(59, 130, 246, 0.28)',
              minHeight: 48,
              fontSize: '0.95rem',
            }
        }
      >
        <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-headset'} fs-6`} />
        {!isCompactSidebar && <span className="fw-semibold sidebar-support-text">Destek</span>}
      </button>
    </div>
  );

  // B2B anlik takip TV ekraninda destek butonu gosterilmiyor.
  if (!portalTarget || isLiveBoardPath(location.pathname)) {
    return null;
  }

  return createPortal(widget, portalTarget);
}
